import React, { useCallback, useEffect, useState } from "react";
import Styles from './styles.module.scss';
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { resetPassword, resetPasswordFailure } from "../../store/main/action";

import pasIcon from "../../theme/assets/svg/b_pas_icon.svg";
import showAIcon from "../../theme/assets/svg/b_pas_show_a_icon.svg";
import showPIcon from "../../theme/assets/svg/b_pas_show_p_icon.svg";

const getErrorText = (error) => {
    if (!error) return null;

    if (Array.isArray(error)) {
        return error[0];
    }

    return error;
};

const ResetMain = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { token, email} = useParams();

    const formErr = useSelector(store => store.main.resetPasswordStatusErr);
    const resetPasswordStatus = useSelector(store => store.main.resetPasswordStatus);
    const resetPasswordStatusStarted = useSelector(store => store.main.resetPasswordStatusStarted);

    const [password, setPassword] = useState('');
    const [password_confirmation, setPassword_confirmation] = useState('');
    const [isShowPassword, setIsShowPassword] = useState(false);

    const passwordError = getErrorText(formErr?.password);
    const passwordConfirmationError = getErrorText(formErr?.password_confirmation);
    const tokenError = getErrorText(formErr?.token);

    const clearFieldError = (fieldName) => {
        if (formErr?.[fieldName]) {
            const updatedErrors = { ...formErr };
            delete updatedErrors[fieldName];

            dispatch(resetPasswordFailure(updatedErrors));
        }
    };

    const sendFormHandler = useCallback(() => {
        if (resetPasswordStatusStarted) return;

        dispatch(resetPassword({
            token,
            email,
            password,
            password_confirmation,
        }));
    }, [
        token,
        email,
        password,
        password_confirmation,
        resetPasswordStatusStarted,
        dispatch,
    ]);

    useEffect(() => {
        if (resetPasswordStatus) {
            navigate('/sign-in');
        }
    }, [resetPasswordStatus, navigate]);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.keyCode === 13) {
                sendFormHandler();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [sendFormHandler]);

    return (
        <section className={Styles.container}>
            <div className={`${Styles.contentWrap} ${Styles.pageContainer}`}>
                <div className={Styles.formWrap}>
                    <div className={Styles.formTitle}>
                        {t('Восстановление пароля')}
                    </div>

                    <div className={Styles.formSubTitle}>
                        {t('Введите новый пароль для вашего аккаунта')}
                    </div>

                    <div className={Styles.contentBox}>
                        <div className={`${Styles.inputWrap} ${passwordError ? Styles.inputWrapWithErr : ''}`}>
                            <div className={Styles.inputTitleRow}>
                                <div className={Styles.inputTitle}>
                                    {t('Новый пароль')}
                                </div>
                            </div>

                            <div className={Styles.inputBox}>
                                <input
                                    type={isShowPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password}
                                    disabled={resetPasswordStatusStarted}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        clearFieldError('password');
                                    }}
                                />

                                <div className={Styles.absLeft}>
                                    <img src={pasIcon} alt={t('Новый пароль')} />
                                </div>

                                <div
                                    className={`${Styles.absRight} ${resetPasswordStatusStarted ? Styles.absRightDisabled : ''}`}
                                    onClick={() => {
                                        if (!resetPasswordStatusStarted) {
                                            setIsShowPassword(!isShowPassword);
                                        }
                                    }}
                                >
                                    <img src={isShowPassword ? showPIcon : showAIcon} alt={t('Показать пароль')} />
                                </div>
                            </div>

                            <div className={`${Styles.tcErr} ${passwordError ? Styles.tcErrActive : ''}`}>
                                {passwordError}
                            </div>
                        </div>

                        <div className={`${Styles.inputWrap} ${passwordConfirmationError ? Styles.inputWrapWithErr : ''}`}>
                            <div className={Styles.inputTitleRow}>
                                <div className={Styles.inputTitle}>
                                    {t('Подтвердите пароль')}
                                </div>
                            </div>

                            <div className={Styles.inputBox}>
                                <input
                                    type={isShowPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password_confirmation}
                                    disabled={resetPasswordStatusStarted}
                                    onChange={(e) => {
                                        setPassword_confirmation(e.target.value);
                                        clearFieldError('password_confirmation');
                                    }}
                                />

                                <div className={Styles.absLeft}>
                                    <img src={pasIcon} alt={t('Подтвердите пароль')} />
                                </div>

                                <div
                                    className={`${Styles.absRight} ${resetPasswordStatusStarted ? Styles.absRightDisabled : ''}`}
                                    onClick={() => {
                                        if (!resetPasswordStatusStarted) {
                                            setIsShowPassword(!isShowPassword);
                                        }
                                    }}
                                >
                                    <img src={isShowPassword ? showPIcon : showAIcon} alt={t('Показать пароль')} />
                                </div>
                            </div>

                            <div className={`${Styles.tcErr} ${passwordConfirmationError ? Styles.tcErrActive : ''}`}>
                                {passwordConfirmationError}
                            </div>
                        </div>

                        <div className={`${Styles.tcErr} ${tokenError ? Styles.tcErrActive : ''}`}>
                            {tokenError}
                        </div>
                    </div>

                    <button
                        type="button"
                        className={`${Styles.mainBtn} ${resetPasswordStatusStarted ? Styles.disabled : ''}`}
                        onClick={sendFormHandler}
                        disabled={resetPasswordStatusStarted}
                    >
                        {resetPasswordStatusStarted ? t('Сохраняем...') : t('Восстановить пароль')}
                    </button>

                    <div className={Styles.sep} />

                    <div className={Styles.note}>
                        {t('Уже есть аккаунт?')}{" "}
                        <span onClick={() => navigate('/sign-in')}>
                            {t('Войти')}
                        </span>
                    </div>
                </div>

                <div className={Styles.imgWrap} />
            </div>
        </section>
    );
};

export default ResetMain;